import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  serverTimestamp,
  writeBatch,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_POINTS_CONFIG } from './leagueService';

export const SANDBOX_USER_IDS = [
  'sandbox_u1', 'sandbox_u2', 'sandbox_u3',
  'sandbox_u4', 'sandbox_u5', 'sandbox_u6',
];

const SANDBOX_NAMES = [
  { username: 'alex_rivera'  },
  { username: 'jordan_kim'   },
  { username: 'sam_patel'    },
  { username: 'morgan_chen'  },
  { username: 'casey_torres' },
  { username: 'riley_nguyen' },
];

/* ── Sandbox lifecycle ──────────────────────────────────────── */

/**
 * Create the sandbox league and seed it. Idempotent — returns
 * existing sandboxLeagueId if already created.
 */
export async function createSandboxLeague(adminUid) {
  const settingsSnap = await getDoc(doc(db, 'settings', 'app'));
  if (settingsSnap.exists() && settingsSnap.data().sandboxLeagueId) {
    return settingsSnap.data().sandboxLeagueId;
  }

  const leagueId = doc(collection(db, 'leagues')).id;

  await setDoc(doc(db, 'leagues', leagueId), {
    name: 'Admin Sandbox',
    commissionerId: adminUid,
    isPublic: false,
    isSandbox: true,
    createdAt: serverTimestamp(),
    memberCount: 1,
    settingsLocked: false,
    pointsConfig: DEFAULT_POINTS_CONFIG,
    inviteCode: 'SANDBOX01',
  });

  // Admin joins as first member
  await setDoc(doc(db, 'leagueMembers', `${leagueId}_${adminUid}`), {
    leagueId,
    userId: adminUid,
    joinedAt: serverTimestamp(),
    totalPoints: 0,
    mpp: 0,
    correctPicks: 0,
    totalPicks: 0,
  });

  // Persist sandboxLeagueId in settings
  await setDoc(doc(db, 'settings', 'app'), { sandboxLeagueId: leagueId }, { merge: true });

  await seedSandboxData(leagueId);
  return leagueId;
}

/**
 * Seed 6 fake users + random group-stage picks into the sandbox league.
 */
export async function seedSandboxData(sandboxLeagueId) {
  const userBatch = writeBatch(db);

  for (let i = 0; i < SANDBOX_USER_IDS.length; i++) {
    const uid = SANDBOX_USER_IDS[i];
    const { username } = SANDBOX_NAMES[i];

    userBatch.set(doc(db, 'users', uid), {
      username,
      email: `${uid}@sandbox.test`,
      role: 'user',
      darkMode: false,
      joinedAt: serverTimestamp(),
    });

    userBatch.set(doc(db, 'leagueMembers', `${sandboxLeagueId}_${uid}`), {
      leagueId: sandboxLeagueId,
      userId: uid,
      joinedAt: serverTimestamp(),
      totalPoints: 0,
      mpp: 0,
      correctPicks: 0,
      totalPicks: 0,
    });
  }

  await userBatch.commit();

  await updateDoc(doc(db, 'leagues', sandboxLeagueId), {
    memberCount: SANDBOX_USER_IDS.length + 1,
  });

  // Seed random picks for all group stage matches (6 users × 72 matches = 432 docs, under batch limit)
  const matchesSnap = await getDocs(
    query(collection(db, 'matches'), where('round', '==', 'group'))
  );
  const matches = matchesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const pickBatch = writeBatch(db);
  for (const match of matches) {
    for (const uid of SANDBOX_USER_IDS) {
      const pickedTeamId = Math.random() < 0.5 ? match.homeTeamId : match.awayTeamId;
      pickBatch.set(doc(db, 'picks', `${sandboxLeagueId}_${uid}_${match.id}`), {
        leagueId: sandboxLeagueId,
        userId: uid,
        matchId: match.id,
        pickedTeamId,
        round: 'group',
        pointsEarned: null,
        isUnique: false,
      });
    }
  }
  await pickBatch.commit();
}

/**
 * Fully delete the sandbox league, all its members, picks, fake users,
 * and clear sandboxLeagueId from settings.
 */
export async function deleteSandbox(sandboxLeagueId) {
  // Delete all picks for this league
  const picksSnap = await getDocs(
    query(collection(db, 'picks'), where('leagueId', '==', sandboxLeagueId))
  );
  if (!picksSnap.empty) {
    const batch = writeBatch(db);
    picksSnap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  // Delete all leagueMembers + fake user docs
  const membersSnap = await getDocs(
    query(collection(db, 'leagueMembers'), where('leagueId', '==', sandboxLeagueId))
  );
  const cleanBatch = writeBatch(db);
  membersSnap.docs.forEach((d) => cleanBatch.delete(d.ref));
  for (const uid of SANDBOX_USER_IDS) {
    cleanBatch.delete(doc(db, 'users', uid));
  }
  cleanBatch.delete(doc(db, 'leagues', sandboxLeagueId));
  await cleanBatch.commit();

  // Clear sandboxLeagueId from settings
  await updateDoc(doc(db, 'settings', 'app'), { sandboxLeagueId: deleteField() });
}

/**
 * Wipe all fake user data from the sandbox and re-seed fresh.
 */
export async function resetSandbox(sandboxLeagueId) {
  // Delete all picks for this league
  const picksSnap = await getDocs(
    query(collection(db, 'picks'), where('leagueId', '==', sandboxLeagueId))
  );
  if (!picksSnap.empty) {
    const deleteBatch = writeBatch(db);
    picksSnap.docs.forEach((d) => deleteBatch.delete(d.ref));
    await deleteBatch.commit();
  }

  // Delete fake leagueMember + user docs
  const cleanBatch = writeBatch(db);
  for (const uid of SANDBOX_USER_IDS) {
    cleanBatch.delete(doc(db, 'leagueMembers', `${sandboxLeagueId}_${uid}`));
    cleanBatch.delete(doc(db, 'users', uid));
  }
  await cleanBatch.commit();

  // Re-seed
  await seedSandboxData(sandboxLeagueId);
}

/* ── Match / round controls ─────────────────────────────────── */

/**
 * Set a match result. Scoring is handled automatically by the
 * scoreOnMatchFinal Cloud Function which triggers on this write.
 */
export async function setMatchResult(matchId, { homeScore, awayScore, winnerId = null }) {
  await updateDoc(doc(db, 'matches', matchId), {
    homeScore,
    awayScore,
    winnerId,
    status: 'final',
  });
}

/**
 * Update the status of a round document.
 */
export async function updateRoundStatus(roundId, status) {
  await updateDoc(doc(db, 'rounds', roundId), { status });
}

/**
 * Undo a previously set match result.
 * - Reverses pointsEarned / isUnique on all picks for this match
 * - Decrements leagueMember totalPoints / correctPicks / totalPicks
 * - Recalculates mpp for affected league members
 * - Clears match result fields and resets status to 'locked'
 */
export async function clearMatchResult(matchId) {
  const matchRef = doc(db, 'matches', matchId);
  const matchSnap = await getDoc(matchRef);
  if (!matchSnap.exists()) throw new Error('Match not found.');
  if (matchSnap.data().status !== 'final') throw new Error('Match is not final — nothing to undo.');

  // Fetch all picks for this match across all leagues
  const picksSnap = await getDocs(
    query(collection(db, 'picks'), where('matchId', '==', matchId))
  );

  // Group picks by leagueId so we can update member stats
  const byLeague = {};
  picksSnap.docs.forEach((d) => {
    const data = d.data();
    if (!byLeague[data.leagueId]) byLeague[data.leagueId] = [];
    byLeague[data.leagueId].push({ ref: d.ref, ...data });
  });

  // Firestore batches are capped at 500 writes — chunk if needed
  const allWrites = [];

  for (const [leagueId, picks] of Object.entries(byLeague)) {
    for (const pick of picks) {
      const pointsEarned = pick.pointsEarned ?? 0;
      const wasCorrect   = pointsEarned > 0;
      const memberRef    = doc(db, 'leagueMembers', `${leagueId}_${pick.userId}`);

      allWrites.push({ ref: pick.ref, data: { pointsEarned: null, isUnique: false } });
      allWrites.push({
        ref: memberRef,
        data: {
          totalPoints:  increment(-pointsEarned),
          correctPicks: increment(wasCorrect ? -1 : 0),
          totalPicks:   increment(-1),
        },
      });
    }
  }

  // Flush in batches of 400
  for (let i = 0; i < allWrites.length; i += 400) {
    const batch = writeBatch(db);
    allWrites.slice(i, i + 400).forEach(({ ref, data }) => batch.update(ref, data));
    await batch.commit();
  }

  // Recalculate mpp for affected leagues
  for (const leagueId of Object.keys(byLeague)) {
    const membersSnap = await getDocs(
      query(collection(db, 'leagueMembers'), where('leagueId', '==', leagueId))
    );
    const mppBatch = writeBatch(db);
    membersSnap.docs.forEach((d) => {
      const m = d.data();
      // totalPoints and totalPicks were already decremented by the prior batch.commit()
      const totalPicks = m.totalPicks ?? 0;
      const mpp = totalPicks > 0 ? (m.totalPoints ?? 0) / totalPicks : 0;
      mppBatch.update(d.ref, { mpp });
    });
    await mppBatch.commit();
  }

  // Reset the match itself
  await updateDoc(matchRef, {
    homeScore:  deleteField(),
    awayScore:  deleteField(),
    winnerId:   deleteField(),
    status:     'locked',
  });
}

/**
 * Admin-side execution of a pending account deletion.
 * Deletes all Firestore data for the user: commissioner leagues (+ their picks/members),
 * own leagueMember records, own picks, user doc, username doc, and pendingDeletion record.
 * Note: Firebase Auth account must be deleted separately via Firebase console.
 */
export async function executeAccountDeletion(uid) {
  // 1. Delete all leagues where this user is commissioner
  const commLeaguesSnap = await getDocs(
    query(collection(db, 'leagues'), where('commissionerId', '==', uid))
  );
  for (const leagueDoc of commLeaguesSnap.docs) {
    const leagueId = leagueDoc.id;
    // Picks for this league
    const picksSnap = await getDocs(
      query(collection(db, 'picks'), where('leagueId', '==', leagueId))
    );
    for (let i = 0; i < picksSnap.docs.length; i += 400) {
      const batch = writeBatch(db);
      picksSnap.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
    // Members for this league
    const membersSnap = await getDocs(
      query(collection(db, 'leagueMembers'), where('leagueId', '==', leagueId))
    );
    if (!membersSnap.empty) {
      const batch = writeBatch(db);
      membersSnap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
    // League doc
    await deleteDoc(leagueDoc.ref);
  }

  // 2. Delete own leagueMember records (leagues user joined but wasn't commissioner of)
  const ownMembersSnap = await getDocs(
    query(collection(db, 'leagueMembers'), where('userId', '==', uid))
  );
  if (!ownMembersSnap.empty) {
    const batch = writeBatch(db);
    ownMembersSnap.docs.forEach((d) => {
      batch.delete(d.ref);
      batch.update(doc(db, 'leagues', d.data().leagueId), { memberCount: increment(-1) });
    });
    await batch.commit();
  }

  // 3. Delete own picks (leagues user was a member of, not commissioner)
  const ownPicksSnap = await getDocs(
    query(collection(db, 'picks'), where('userId', '==', uid))
  );
  for (let i = 0; i < ownPicksSnap.docs.length; i += 400) {
    const batch = writeBatch(db);
    ownPicksSnap.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  // 4. Delete user doc and username entry
  const userSnap = await getDoc(doc(db, 'users', uid));
  if (userSnap.exists()) {
    const { username } = userSnap.data();
    if (username) await deleteDoc(doc(db, 'usernames', username.toLowerCase()));
  }
  await deleteDoc(doc(db, 'users', uid));

  // 5. Mark pendingDeletion as executed
  await updateDoc(doc(db, 'pendingDeletions', uid), { status: 'executed' });
}
